import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react";
import { useLang } from "@/i18n";

const kols = [
  {
    handle: "futcrunch",
    name: "Futcrunch",
    realName: "Santiago Vazquez (Venezuela)",
    avatar: "/images/kol_futcrunch.jpg",
    subscribers: "17.41M",
    views: "9.3B+",
    category: "Football · Sports",
    region: "🌍 Global",
    platform: "YouTube",
    icon: "mdi:soccer",
    color: "#22c55e",
    url: "https://www.youtube.com/@futcrunch/videos",
    desc: "One of the world's top football entertainment creators, famous for creative FIFA challenges and authentic collaborations with football superstars—content with massive viral potential.",
    brands: ["DAZN", "EA Sports FC", "Top Eleven", "Airup"],
    highlight: "Has collaborated on-screen with Ronaldo, Haaland, and other global football icons—reaching fans across every continent.",
  },
  {
    handle: "zhong",
    name: "Zhong",
    realName: "Zhongni Zhu",
    avatar: "/images/kol_zhong.jpg",
    subscribers: "70M",
    views: "23.4B+",
    category: "Lifestyle · Entertainment",
    region: "🇺🇸 North America",
    platform: "YouTube",
    icon: "mdi:play-circle",
    color: "#f59e0b",
    url: "https://www.youtube.com/@zhong/videos",
    desc: "A top-tier YouTube entertainment creator who started with family comedy shorts—crossing age groups, languages, and cultures with a massive, broad-appeal audience.",
    brands: ["Studio71", "Amazon Prime", "Roku"],
    highlight: "Signed a global ad sales partnership with Studio71, reaching 95M+ followers across platforms.",
  },
  {
    handle: "Mrwhosetheboss",
    name: "Mrwhosetheboss",
    realName: "Arun Maini (UK)",
    avatar: "/images/kol_mrwhosetheboss.jpg",
    subscribers: "22.4M",
    views: "8.5B+",
    category: "Tech Reviews",
    region: "🇬🇧 Europe",
    platform: "YouTube",
    icon: "mdi:cellphone",
    color: "#3b82f6",
    url: "https://www.youtube.com/@Mrwhosetheboss/videos",
    desc: "One of the world's most influential tech review YouTubers, holding a Guinness World Record, with in-depth coverage of Samsung, Apple, OnePlus, and other major brands.",
    brands: ["Samsung", "OnePlus", "Apple (reviews)", "Xiaomi", "Huawei"],
    highlight: "Owns the world's largest physical smartphone collection—his deep-dive reviews directly influence global tech purchase decisions.",
  },
  {
    handle: "lazarbeam",
    name: "LazarBeam",
    realName: "Lannan Eacott (Australia)",
    avatar: "/images/kol_lazarbeam.jpg",
    subscribers: "23.3M",
    views: "6.5B+",
    category: "Gaming",
    region: "🇦🇺 Oceania",
    platform: "YouTube",
    icon: "mdi:controller-classic",
    color: "#a855f7",
    url: "https://www.youtube.com/lazarbeam/videos",
    desc: "Australia's premier gaming creator and a benchmark KOL in the Fortnite space—winning a massive gamer following with his comedic style and extreme challenge content.",
    brands: ["GamerSupps", "Kellogg's", "Fortnite (Epic)", "Studio71"],
    highlight: "Epic Games officially released a Fortnite skin in his likeness—extremely high endorsement value reaching core gaming audiences worldwide.",
  },
  {
    handle: "Aphmau",
    name: "Aphmau",
    realName: "Jessica Bravura (USA)",
    avatar: "/images/kol_aphmau.jpg",
    subscribers: "24.7M",
    views: "27.4B+",
    category: "Gaming",
    region: "🇺🇸 North America",
    platform: "YouTube",
    icon: "mdi:minecraft",
    color: "#10b981",
    url: "https://www.youtube.com/@Aphmau/videos",
    desc: "One of Minecraft's most-loved female creators, attracting a large female and teen audience through story-driven gameplay and role-play video series.",
    brands: ["Claire's", "Minecraft (Microsoft)", "Catface Media"],
    highlight: "Ongoing deep collaboration with Claire's has produced co-branded accessories and toy lines—demonstrating outstanding commercial monetization capability.",
  },
  {
    handle: "jeenie.weenie",
    name: "Jeenie Weenie",
    realName: "Jeenie (Canada)",
    avatar: "/images/kol_jeenie.jpg",
    subscribers: "12.9M",
    views: "10B+",
    category: "Comedy · Lifestyle",
    region: "🇨🇦 North America",
    platform: "YouTube / Shorts",
    icon: "mdi:airplane",
    color: "#ec4899",
    url: "https://www.youtube.com/@jeenie.weenie/videos",
    desc: "A former flight attendant turned comedy creator—her cabin-life storytelling Shorts consistently go viral, reaching a broad female and professional audience.",
    brands: ["YouTube Creators", "Netflix (Bridgerton promo)", "Kore Limited (collab apparel)"],
    highlight: "Invited by Netflix to participate in the global Bridgerton campaign—consistently expanding social reach.",
  },
  {
    handle: "NichLmao",
    name: "NichLmao",
    realName: "Nicholai Perrett (USA)",
    avatar: "/images/kol_nichlmao.jpg",
    subscribers: "40.4M",
    views: "19.9B+",
    category: "Comedy · Entertainment",
    region: "🇺🇸 North America",
    platform: "YouTube",
    icon: "mdi:emoticon-outline",
    color: "#f97316",
    url: "https://www.youtube.com/@NichLmao/videos",
    desc: "A top-400 YouTube comedy creator with exceptionally high engagement rates and fan loyalty—his recent crossover into professional boxing events has sent his exposure skyward.",
    brands: ["MisFits Boxing", "Multiple consumer brands"],
    highlight: "North America's top comedy creator with global reach—boxing event crossover has dramatically boosted commercial value.",
  },
  {
    handle: "BraveWilderness",
    name: "Brave Wilderness",
    realName: "Coyote Peterson (USA)",
    avatar: "/images/kol_coyote.jpg",
    subscribers: "21.6M",
    views: "5.4B+",
    category: "Wildlife · Nature",
    region: "🇺🇸 North America",
    platform: "YouTube",
    icon: "mdi:paw",
    color: "#84cc16",
    url: "https://www.youtube.com/@BraveWilderness/videos",
    desc: "The world's largest wildlife digital content brand—Emmy Award winner combining deep-dive science education with extreme adventure, attracting huge family and nature-enthusiast audiences.",
    brands: ["Animal Planet", "Columbus Blue Jackets (NHL)", "Multiple outdoor & nature brands"],
    highlight: "Emmy Award winner with an official Animal Planet documentary collaboration—the first-choice KOL for outdoor and nature brands.",
  },
  {
    handle: "Browney",
    name: "Browney",
    realName: "Stan Browney (USA)",
    avatar: "/images/kol_browney.jpg",
    subscribers: "12M+",
    views: "Billions",
    category: "Fitness · Sports",
    region: "🇺🇸 North America",
    platform: "YouTube",
    icon: "mdi:dumbbell",
    color: "#06b6d4",
    url: "https://www.youtube.com/@Browney/videos",
    desc: "A top fitness creator specializing in street workout and challenge content—winning global fitness enthusiasts with his humor and high-quality training videos.",
    brands: ["Gymshark (long-term ambassador)", "MyProtein", "Multiple sports nutrition brands"],
    highlight: "Official long-term Gymshark brand ambassador with exclusive discount code STAN10—exceptionally high repeat-purchase conversion among fans.",
  },
  {
    handle: "BestEverFoodReviewShow",
    name: "Best Ever Food Review Show",
    realName: "Sonny Side (USA)",
    avatar: "/images/kol_befrs.jpg",
    subscribers: "12M",
    views: "4.5B+",
    category: "Food & Travel",
    region: "🌏 Asia-Pacific / Global",
    platform: "YouTube",
    icon: "mdi:food",
    color: "#ef4444",
    url: "https://www.youtube.com/@BestEverFoodReviewShow/videos",
    desc: "A unique global food exploration channel focused on niche culinary cultures across Asia-Pacific and beyond—attracting worldwide food and travel enthusiasts with documentary-quality content.",
    brands: ["Tourism Board partnerships (multiple countries)", "Food brand integrations", "First We Feast collab"],
    highlight: "The go-to partner for global tourism boards and food brands—audience spans Asia-Pacific and the West, with a talent for cultural fusion and localized content.",
  },
];

const ease = [0.4, 0, 0.2, 1];

export default function KOLShowcase() {
  const { t } = useLang();
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    setCanScrollLeft(scrollRef.current.scrollLeft > 0);
    setCanScrollRight(
      scrollRef.current.scrollLeft < scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 4
    );
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener("scroll", checkScroll); checkScroll(); }
    return () => { if (el) el.removeEventListener("scroll", checkScroll); };
  }, []);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "right" ? 420 : -420, behavior: "smooth" });
  };

  const selectedKol = selected !== null ? kols[selected] : null;

  return (
    <section id="kols" style={{ background: "#080808", overflow: "hidden" }}>
      <div style={{ padding: "7rem 0 0" }}>

        {/* Header */}
        <div className="max-w-7xl mx-auto" style={{ padding: "0 3rem", marginBottom: "4rem" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "2rem" }}>
            <div>
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease }}
                style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}
              >
                <div style={{ width: 32, height: 1.5, background: "#f4440c" }} />
                <span style={{ fontSize: "0.82rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f4440c", fontWeight: 400 }}>
                  {t.kols.eyebrow}
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.1, ease }}
                style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontSize: "clamp(2.8rem, 5vw, 4.2rem)",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.05,
                  letterSpacing: "-0.01em",
                }}
              >
                {t.kols.headline1}<br />
                <em style={{ fontStyle: "italic", color: "#f4440c", fontWeight: 300 }}>{t.kols.headline2}</em>
              </motion.h2>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2, ease }}
              style={{ display: "flex", gap: "0.75rem", flexShrink: 0 }}
            >
              {["left", "right"].map((dir) => {
                const can = dir === "left" ? canScrollLeft : canScrollRight;
                return (
                  <button
                    key={dir}
                    onClick={() => scroll(dir as "left" | "right")}
                    disabled={!can}
                    style={{
                      width: 44, height: 44,
                      border: `1px solid ${can ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.06)"}`,
                      background: "none", cursor: can ? "pointer" : "default",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: can ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.2)",
                      transition: "all 0.3s",
                    }}
                    onMouseEnter={e => { if (can) (e.currentTarget as HTMLElement).style.borderColor = "#f4440c"; }}
                    onMouseLeave={e => { if (can) (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.18)"; }}
                  >
                    <Icon icon={`mdi:chevron-${dir}`} style={{ fontSize: 20 }} />
                  </button>
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* Cards scroll */}
        <div
          ref={scrollRef}
          style={{
            display: "flex",
            gap: "1px",
            overflowX: "auto",
            paddingBottom: 0,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {kols.map((kol, i) => (
            <motion.button
              key={kol.handle}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: Math.min(i * 0.06, 0.35), ease }}
              onClick={() => setSelected(selected === i ? null : i)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                flexShrink: 0,
                width: 210,
                background: selected === i ? "rgba(244,68,12,0.08)" : hovered === i ? "rgba(255,255,255,0.03)" : "transparent",
                border: "none",
                borderRight: "1px solid rgba(255,255,255,0.06)",
                cursor: "pointer",
                padding: "0",
                textAlign: "left",
                transition: "background 0.3s",
                position: "relative",
              }}
            >
              {/* Avatar full-width — unified square style */}
              <div style={{ position: "relative", width: "100%", paddingTop: "100%", overflow: "hidden", background: "#111" }}>
                <img
                  src={kol.avatar}
                  alt={kol.name}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center top",
                    filter: selected === i
                      ? "grayscale(0) brightness(1.05) contrast(1.05)"
                      : hovered === i
                      ? "grayscale(10%) brightness(0.95)"
                      : "grayscale(30%) brightness(0.8) contrast(1.02)",
                    transition: "filter 0.5s ease, transform 0.6s ease",
                    transform: hovered === i ? "scale(1.04)" : "scale(1)",
                  }}
                />
                {/* Gradient overlay */}
                <div style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to top, rgba(8,8,8,0.9) 0%, rgba(8,8,8,0.2) 50%, transparent 100%)",
                }} />
                {/* Category badge */}
                <div style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  background: `${kol.color}22`,
                  border: `1px solid ${kol.color}55`,
                  padding: "0.2rem 0.5rem",
                  fontSize: "0.62rem",
                  color: kol.color,
                  letterSpacing: "0.06em",
                  fontWeight: 500,
                }}>
                  {kol.category.split(" · ")[1]}
                </div>
                {/* Selected indicator */}
                {selected === i && (
                  <motion.div
                    layoutId="activeIndicator"
                    style={{
                      position: "absolute",
                      bottom: 0, left: 0, right: 0,
                      height: 2,
                      background: "#f4440c",
                    }}
                  />
                )}
              </div>

              {/* Info */}
              <div style={{ padding: "1rem 1rem 1.25rem" }}>
                <div style={{
                  fontSize: "0.92rem",
                  fontWeight: 600,
                  color: selected === i ? "#fff" : "rgba(255,255,255,0.82)",
                  marginBottom: "0.2rem",
                  lineHeight: 1.3,
                  transition: "color 0.3s",
                }}>
                  {kol.name}
                </div>
                <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", marginBottom: "0.75rem", letterSpacing: "0.02em" }}>
                  {kol.realName}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <div>
                    <div style={{
                      fontFamily: "'Inter Tight', sans-serif",
                      fontSize: "1.35rem",
                      fontWeight: 400,
                      color: "#f4440c",
                      lineHeight: 1,
                      letterSpacing: "-0.02em",
                    }}>
                      {kol.subscribers}
                    </div>
                    <div style={{ fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginTop: "0.15rem" }}>
                      {t.kols.subscribers}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.38)", fontWeight: 400 }}>{kol.views}</div>
                    <div style={{ fontSize: "0.6rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", marginTop: "0.15rem" }}>{t.kols.views}</div>
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Detail Panel */}
        <AnimatePresence>
          {selectedKol && (
            <motion.div
              key={selected}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease }}
              style={{ overflow: "hidden", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="max-w-7xl mx-auto" style={{ padding: "3rem 3rem 4rem" }}>
                <div className="kol-detail-grid">

                  {/* Left: large avatar */}
                  <div>
                    <div style={{ position: "relative", overflow: "hidden", marginBottom: "1.25rem" }}>
                      <img
                        src={selectedKol.avatar}
                        alt={selectedKol.name}
                        style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", objectPosition: "top", display: "block" }}
                      />
                      <div style={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(to top, rgba(8,8,8,0.7) 0%, transparent 60%)",
                      }} />
                      <a
                        href={selectedKol.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          bottom: 14,
                          left: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          background: "#f4440c",
                          color: "#fff",
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          padding: "0.4rem 0.85rem",
                          textDecoration: "none",
                          letterSpacing: "0.06em",
                        }}
                      >
                        <Icon icon="mdi:youtube" style={{ fontSize: 14 }} />
                        {t.kols.viewChannel}
                      </a>
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", letterSpacing: "0.06em" }}>
                      {selectedKol.region} · {selectedKol.platform}
                    </div>
                  </div>

                  {/* Middle: bio */}
                  <div>
                    <div style={{
                      fontSize: "0.72rem",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "#f4440c",
                      marginBottom: "0.75rem",
                    }}>
                      {selectedKol.category}
                    </div>
                    <h3 style={{
                      fontFamily: "'Inter Tight', sans-serif",
                      fontSize: "2.2rem",
                      fontWeight: 400,
                      color: "#fff",
                      lineHeight: 1.1,
                      marginBottom: "0.4rem",
                    }}>
                      {selectedKol.name}
                    </h3>
                    <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.4)", marginBottom: "1.5rem" }}>
                      {selectedKol.realName}
                    </div>
                    <div style={{ height: 1, background: "rgba(255,255,255,0.07)", marginBottom: "1.5rem" }} />
                    <p style={{
                      fontSize: "0.98rem",
                      lineHeight: 1.85,
                      color: "rgba(255,255,255,0.65)",
                      fontWeight: 400,
                      marginBottom: "1.75rem",
                    }}>
                      {selectedKol.desc}
                    </p>
                    {/* Highlight */}
                    <div style={{
                      background: "rgba(244,68,12,0.06)",
                      border: "1px solid rgba(244,68,12,0.18)",
                      padding: "1rem 1.25rem",
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "flex-start",
                    }}>
                      <Icon icon="mdi:lightbulb-outline" style={{ fontSize: 16, color: "#f4440c", flexShrink: 0, marginTop: "0.1rem" }} />
                      <span style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.75, fontWeight: 400 }}>
                        {selectedKol.highlight}
                      </span>
                    </div>
                  </div>

                  {/* Right: stats + brands */}
                  <div>
                    {/* Stats */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      border: "1px solid rgba(255,255,255,0.07)",
                      marginBottom: "2rem",
                    }}>
                      {[
                        { label: t.kols.subscribers, value: selectedKol.subscribers },
                        { label: t.kols.views, value: selectedKol.views },
                      ].map((s, i) => (
                        <div key={i} style={{
                          padding: "1.25rem 1.5rem",
                          borderRight: i === 0 ? "1px solid rgba(255,255,255,0.07)" : "none",
                        }}>
                          <div style={{
                            fontFamily: "'Inter Tight', sans-serif",
                            fontSize: "1.8rem",
                            fontWeight: 400,
                            color: "#f4440c",
                            lineHeight: 1,
                            letterSpacing: "-0.02em",
                          }}>
                            {s.value}
                          </div>
                          <div style={{ fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginTop: "0.4rem" }}>
                            {s.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Brand collaborations */}
                    <div style={{
                      fontSize: "0.72rem",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                      marginBottom: "1rem",
                    }}>
                      {t.kols.brandPartners}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      {selectedKol.brands.map((brand) => (
                        <span
                          key={brand}
                          style={{
                            fontSize: "0.82rem",
                            color: "rgba(255,255,255,0.68)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            padding: "0.35rem 0.85rem",
                            fontWeight: 400,
                            letterSpacing: "0.02em",
                          }}
                        >
                          {brand}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.35)", marginBottom: "1rem", lineHeight: 1.7 }}>
                        {t.kols.kolCtaText}
                      </p>
                      <button
                        onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          background: "none",
                          border: "1px solid rgba(244,68,12,0.5)",
                          color: "#f4440c",
                          fontSize: "0.82rem",
                          fontWeight: 500,
                          padding: "0.65rem 1.4rem",
                          cursor: "pointer",
                          letterSpacing: "0.06em",
                          transition: "all 0.3s",
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.background = "#f4440c";
                          (e.currentTarget as HTMLElement).style.color = "#fff";
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.background = "none";
                          (e.currentTarget as HTMLElement).style.color = "#f4440c";
                        }}
                      >
                        {t.kols.getProposal}
                        <Icon icon="mdi:arrow-right" style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="max-w-7xl mx-auto"
          style={{
            padding: "2rem 3rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <div style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.35)", fontWeight: 400 }}>
            {t.kols.resourceNote}
          </div>
          <button
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              color: "#f4440c", fontSize: "0.88rem", fontWeight: 400,
              background: "none", border: "none", cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            {t.kols.getFullList}
            <Icon icon="mdi:arrow-right" style={{ fontSize: 14 }} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
